import { Check, Trash, X } from 'lucide-react';
import React, { useState } from 'react';
import {
  StreamServeProjekt,
  useDeleteStreamServeProjekt,
  useUpdateStreamServeProjekt,
} from '@/adapter/opm-cockpit-backend/useStreamServeProjekte';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface Props {
  projekt: StreamServeProjekt;
}

export const EditStreamServeProjektRow: React.FC<Props> = ({ projekt }) => {
  const [editing, setEditing] = useState(false);
  const [projektName, setProjektName] = useState(projekt.projektName);
  const [projektVersion, setProjektVersion] = useState(projekt.projektVersion);

  const updateMutation = useUpdateStreamServeProjekt();
  const deleteMutation = useDeleteStreamServeProjekt();

  const onCancel = () => {
    setProjektName(projekt.projektName);
    setProjektVersion(projekt.projektVersion);
    setEditing(false);
  };

  const onSave = () => {
    updateMutation.mutate(
      {
        externalId: projekt.externalId,
        projektName,
        projektVersion,
      },
      {
        onSuccess: () => setEditing(false),
      },
    );
  };

  if (!editing) {
    return (
      <tr className="border-transparent transition-colors border-l-4 hover:border-primary hover:bg-table-hover">
        <td className="p-3 text-sm text-text-primary">{projekt.projektName}</td>
        <td className="p-3 text-sm text-text-primary">
          {projekt.projektVersion}
        </td>
        <td className="p-3">
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
              Bearbeiten
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                  disabled={deleteMutation.isPending}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>

              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Sind Sie wirklich sicher?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Möchten Sie das StreamServe-Projekt{' '}
                    <i>{projekt.projektName}</i> wirklich löschen?
                    <br />
                    <br />
                    Das Projekt kann nur gelöscht werden, solange kein
                    Dokumenttyp es noch referenziert. Diese Aktion wird sofort
                    ausgeführt.
                  </AlertDialogDescription>
                </AlertDialogHeader>

                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => deleteMutation.mutate(projekt.externalId)}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    Löschen
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-transparent border-l-4 bg-table-hover">
      <td className="p-3">
        <input
          value={projektName}
          onChange={(e) => setProjektName(e.target.value)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
        />
      </td>
      <td className="p-3">
        <input
          type="number"
          min={0}
          value={projektVersion}
          onChange={(e) => setProjektVersion(Number(e.target.value))}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
        />
      </td>
      <td className="p-3">
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onSave}
            disabled={updateMutation.isPending}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
};
